import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import "./BetaPublicUI.css";
import {
    BETA_THEME_CHANGE_EVENT,
    clearBetaThemeBootstrap,
    getBetaEffectiveTheme,
    getBetaThemePreference,
} from "./betaTheme";

const BetaPublicThemeContext = createContext(null);

export function useBetaPublicTheme() {
    return useContext(BetaPublicThemeContext);
}

export default function BetaPublicShell({ children, forceDark = false, immersive = false }) {
    const [theme, setTheme] = useState(() => (
        forceDark ? "dark" : getBetaEffectiveTheme(getBetaThemePreference())
    ));

    useEffect(() => {
        if (forceDark) {
            setTheme("dark");
            return undefined;
        }

        const updateTheme = (preference = getBetaThemePreference()) => {
            setTheme(getBetaEffectiveTheme(preference));
        };
        const handleThemeChange = (event) => updateTheme(event.detail?.preference);
        const mediaQuery = typeof window.matchMedia === "function"
            ? window.matchMedia("(prefers-color-scheme: dark)")
            : null;
        const handleSystemThemeChange = () => {
            if (getBetaThemePreference() === "system") updateTheme("system");
        };

        window.addEventListener(BETA_THEME_CHANGE_EVENT, handleThemeChange);
        mediaQuery?.addEventListener?.("change", handleSystemThemeChange);
        return () => {
            window.removeEventListener(BETA_THEME_CHANGE_EVENT, handleThemeChange);
            mediaQuery?.removeEventListener?.("change", handleSystemThemeChange);
        };
    }, [forceDark]);

    useLayoutEffect(() => {
        const themeClass = `beta-public-theme-${theme}`;
        const pageRoots = [document.documentElement, document.body];
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        pageRoots.forEach((element) => {
            element.classList.add("beta-public-active", themeClass);
            if (immersive) element.classList.add("beta-public-immersive");
        });
        themeColorMeta?.setAttribute("content", theme === "dark" ? "#101010" : "#f7f7f7");
        clearBetaThemeBootstrap();
        return () => {
            pageRoots.forEach((element) => {
                element.classList.remove(
                    "beta-public-active",
                    "beta-public-theme-light",
                    "beta-public-theme-dark",
                    "beta-public-immersive"
                );
            });
            if (themeColorMeta) {
                themeColorMeta.setAttribute("content", "#020617");
            }
        };
    }, [immersive, theme]);

    const contextValue = useMemo(() => theme, [theme]);
    const className = [
        "beta-public-shell",
        `beta-public-shell--${theme}`,
        immersive ? "beta-public-shell--immersive" : "",
    ].filter(Boolean).join(" ");

    return (
        <BetaPublicThemeContext.Provider value={contextValue}>
            <div className={className}>{children}</div>
        </BetaPublicThemeContext.Provider>
    );
}
